import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import { notifyUser } from '@/store/Toast';
import { csvFileToArray, readCSVFile } from '@/utils/csv';

export interface ICSVUploadModalProps {
  onHide: () => void;
  onSubmit: (data: Record<string, any>[]) => void;
  title?: string;
  isSubmitting?: boolean;
}

export const CSVUploadModal = (props: ICSVUploadModalProps) => {
  const { onHide, onSubmit, title = 'Upload CSV', isSubmitting = false } = props;
  const dispatch = useDispatch();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<Record<string, any>[] | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setError(null);
      setParsedData(null);
      setHeaders([]);

      if (!file) {
        setSelectedFile(null);
        return;
      }

      // Validate file extension
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension !== 'csv') {
        setError('Please select a valid CSV file.');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setIsParsing(true);

      try {
        const fileContent = await readCSVFile(file);
        const jsonData = csvFileToArray(fileContent as string);

        if (jsonData && jsonData.length > 0) {
          const extractedHeaders = Object.keys(jsonData[0]);
          setHeaders(extractedHeaders);
          setParsedData(jsonData);
          dispatch(
            notifyUser({
              showToast: true,
              toastMessage: `Successfully parsed ${jsonData.length} records from CSV.`,
              toastType: 'success',
            })
          );
        } else {
          setError('No data found in the CSV file.');
          setParsedData(null);
        }
      } catch (_err) {
        setError('Failed to parse CSV file. Please check the file format.');
        setParsedData(null);
      } finally {
        setIsParsing(false);
      }
    },
    [dispatch]
  );

  const handleSubmit = () => {
    if (parsedData && parsedData.length > 0) {
      onSubmit(parsedData);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setParsedData(null);
    setHeaders([]);
    setError(null);
    onHide();
  };

  return (
    <Modal width='max-w-4xl'>
      <h3 className='mb-4 text-lg font-semibold'>{title}</h3>

      <div className='space-y-4'>
        {/* File Input */}
        <div className='space-y-2'>
          <label className='block text-sm font-medium text-gray-700'>Select CSV File</label>
          <input
            type='file'
            accept='.csv'
            onChange={handleFileChange}
            className='block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primaryColor-lighter file:text-primaryColor-normal hover:file:bg-primaryColor-light cursor-pointer'
          />
          {selectedFile && (
            <p className='text-sm text-gray-600'>
              Selected: <span className='font-medium'>{selectedFile.name}</span>
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
            <p className='text-sm text-red-600'>{error}</p>
          </div>
        )}

        {/* Parsing Indicator */}
        {isParsing && (
          <div className='p-3 bg-blue-50 border border-blue-200 rounded-md'>
            <p className='text-sm text-blue-600'>Parsing CSV file...</p>
          </div>
        )}

        {/* Parsed Data Preview */}
        {parsedData && parsedData.length > 0 && (
          <div className='space-y-2'>
            <div className='flex justify-between items-center'>
              <h4 className='text-sm font-medium text-gray-700'>Preview ({parsedData.length} records)</h4>
            </div>
            <div className='max-h-96 overflow-auto border border-gray-200 rounded-md'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50 sticky top-0'>
                  <tr>
                    {headers.map((header, index) => (
                      <th key={index} className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {parsedData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {headers.map((header, colIndex) => (
                        <td key={colIndex} className='px-4 py-2 text-sm text-gray-900 whitespace-nowrap'>
                          {row[header] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className='flex gap-4 pt-5 justify-end'>
          <Button label='Cancel' btnStyle='neutral' onClick={handleCancel} />
          <Button
            label={isSubmitting ? 'Submitting...' : 'Submit'}
            btnStyle='primary'
            onClick={handleSubmit}
            disabled={!parsedData || parsedData.length === 0 || isSubmitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CSVUploadModal;
